import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * useCurrentContext — derive the current agent context from the route.
 *
 * Pure hook (no external state). Reads useLocation() and pattern-matches the
 * pathname; useParams() can't be called outside a matched <Route>, so we do
 * the matching ourselves to keep this usable from any depth.
 *
 * Per ADR-017 §3.3.
 *
 * Returns:
 *   currentPage:       'home' | 'portfolio' | 'portfolio_onboarding' |
 *                      'deals_list' | 'deal' |
 *                      'property' | 'property_search' |
 *                      'suburb_search' | 'strategy' | 'settings' |
 *                      'terms' | 'privacy' | 'engine_debug' | 'unknown'
 *   currentObjectId:   string | null    — :propertyId or :dealId from the URL
 *   currentObjectType: 'property' | 'deal' | 'suburb' | 'portfolio' | null
 */
export function useCurrentContext() {
  const { pathname } = useLocation();

  return useMemo(() => {
    // Object-detail routes (extract id)
    const propertyMatch = pathname.match(/^\/property\/([^/]+)/);
    if (propertyMatch) {
      return {
        currentPage: 'property',
        currentObjectId: propertyMatch[1],
        currentObjectType: 'property',
      };
    }

    const dealMatch = pathname.match(/^\/deals\/([^/]+)/);
    if (dealMatch) {
      return {
        currentPage: 'deal',
        currentObjectId: dealMatch[1],
        currentObjectType: 'deal',
      };
    }

    // Portfolio family
    if (pathname === '/portfolio') {
      return {
        currentPage: 'portfolio',
        currentObjectId: null,
        currentObjectType: 'portfolio',
      };
    }
    if (pathname.startsWith('/portfolio/onboarding')) {
      return {
        currentPage: 'portfolio_onboarding',
        currentObjectId: null,
        currentObjectType: null,
      };
    }

    // Other top-level pages
    const TOP_LEVEL = {
      '/':              { currentPage: 'home',            currentObjectType: null },
      '/deals':         { currentPage: 'deals_list',      currentObjectType: null },
      '/strategy':      { currentPage: 'strategy',        currentObjectType: 'portfolio' },
      '/settings':      { currentPage: 'settings',        currentObjectType: null },
      '/suburb-search': { currentPage: 'suburb_search',   currentObjectType: null },
      '/prop-search':   { currentPage: 'property_search', currentObjectType: null },
      '/terms':         { currentPage: 'terms',           currentObjectType: null },
      '/privacy':       { currentPage: 'privacy',         currentObjectType: null },
      '/debug/engine':  { currentPage: 'engine_debug',    currentObjectType: null },
    };
    if (TOP_LEVEL[pathname]) {
      return { ...TOP_LEVEL[pathname], currentObjectId: null };
    }

    return { currentPage: 'unknown', currentObjectId: null, currentObjectType: null };
  }, [pathname]);
}
